import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, AlertCircle, X } from 'lucide-react';

export interface FloatingAlertProps {
  message: string;
  title?: string;
  variant?: 'destructive' | 'warning' | 'info' | 'success';
  onClose?: () => void;
  autoClose?: boolean;
  autoCloseDuration?: number;
  isVisible?: boolean;
}

export const FloatingAlert: React.FC<FloatingAlertProps> = ({
  message,
  title,
  variant = 'destructive',
  onClose,
  autoClose = true,
  autoCloseDuration = 3000,
  isVisible = true,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShow(true);
    }
  }, [isVisible]);

  useEffect(() => {
    if (autoClose && show && onClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDuration, show]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: AlertTriangle,
          iconColor: 'text-yellow-600',
          titleColor: 'text-yellow-900',
          messageColor: 'text-yellow-700',
          closeColor: 'text-yellow-500 hover:text-yellow-700',
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: Info,
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-900',
          messageColor: 'text-blue-700',
          closeColor: 'text-blue-500 hover:text-blue-700',
        };
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: CheckCircle2,
          iconColor: 'text-green-600',
          titleColor: 'text-green-900',
          messageColor: 'text-green-700',
          closeColor: 'text-green-500 hover:text-green-700',
        };
      case 'destructive':
      default:
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: AlertCircle,
          iconColor: 'text-red-600',
          titleColor: 'text-red-900',
          messageColor: 'text-red-700',
          closeColor: 'text-red-500 hover:text-red-700',
        };
    }
  };

  if (!isVisible) return null;

  const styles = getVariantStyles();
  const IconComponent = styles.icon;

  return (
    <div
      className={`
        fixed top-4 left-1/2 transform -translate-x-1/2 z-50 
        max-w-md w-full mx-4 transition-all duration-300 ease-in-out
        ${show ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
      `}
      role="alert"
    >
      <div
        className={`
          ${styles.bg} ${styles.border} 
          border rounded-lg shadow-lg p-4
        `}
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 ${styles.iconColor}`}>
            <IconComponent className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            {title && (
              <h4 className={`font-semibold ${styles.titleColor} text-sm mb-1`}>
                {title}
              </h4>
            )}
            <p className={`${styles.messageColor} text-sm leading-relaxed`}>
              {message}
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`
              flex-shrink-0 ${styles.closeColor} 
              transition-colors duration-200 
              hover:bg-white hover:bg-opacity-20 
              rounded p-1
            `}
            aria-label="Close alert"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}; 